/**
 * Returns Wizard — Santa Eulalia
 * Multi-step return request form
 * All user-facing strings loaded from window.returnsConfig.translations (Liquid)
 */
(function () {
  'use strict';

  class ReturnsWizard {
    constructor() {
      this.currentStep = 1;
      this.orderData = null;
      this.selectedItems = [];
      this.reasons = {};
      this.address = {};
      this.dhlResult = null;
      this.dhlOption = null;
      // Permite pasar al resumen sin método elegido SOLO cuando DHL falla o
      // no devuelve opciones (gestión manual); en el resto de casos el botón
      // "Siguiente" del paso 4 queda bloqueado hasta elegir método.
      this.dhlManualAllowed = false;
      this.pickupDate = null;
      this.pickupWindow = null;
      this.pickupFee = null;
      this.policyAccepted = false;
      this.servicePoints = [];          // puntos DHL del último dhl-check
      this.selectedServicePoint = null; // punto elegido (QR/etiqueta drop-off)

      this.config = window.returnsConfig || {};
      this.translations = this.config.translations || {};
      this.locale = this.config.locale || 'es';

      this.RETURN_REASONS = [
        { value: '', label: this.t('reason_placeholder') },
        { value: 'wrong_size', label: this.t('reason_wrong_size') },
        { value: 'defective', label: this.t('reason_defective') },
        { value: 'not_as_described', label: this.t('reason_not_as_described') },
        { value: 'not_expected', label: this.t('reason_not_expected') },
        { value: 'wrong_product', label: this.t('reason_wrong_product') },
        { value: 'other', label: this.t('reason_other') }
      ];

      // Map DHL API day keys (full English + abbreviated) to translated labels
      this.dayMap = {
        MONDAY: this.t('day_mon'), Monday: this.t('day_mon'), Mon: this.t('day_mon'),
        TUESDAY: this.t('day_tue'), Tuesday: this.t('day_tue'), Tue: this.t('day_tue'),
        WEDNESDAY: this.t('day_wed'), Wednesday: this.t('day_wed'), Wed: this.t('day_wed'),
        THURSDAY: this.t('day_thu'), Thursday: this.t('day_thu'), Thu: this.t('day_thu'),
        FRIDAY: this.t('day_fri'), Friday: this.t('day_fri'), Fri: this.t('day_fri'),
        SATURDAY: this.t('day_sat'), Saturday: this.t('day_sat'), Sat: this.t('day_sat'),
        SUNDAY: this.t('day_sun'), Sunday: this.t('day_sun'), Sun: this.t('day_sun')
      };

      // Lista de países soportados para devoluciones (ISO 3166-1 alpha-2).
      // Sincronizada con VALID_ISO_COUNTRIES del backend. Si añades países
      // aquí, añádelos también allí (services/ReturnsCommandController).
      this.SUPPORTED_COUNTRIES = [
        'AD', 'AE', 'AR', 'AT', 'AU', 'BE', 'BG', 'BR', 'CA', 'CH',
        'CN', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB',
        'GR', 'HK', 'HR', 'HU', 'IE', 'IL', 'IN', 'IT', 'JP', 'KR',
        'LT', 'LU', 'LV', 'MA', 'MC', 'MT', 'MX', 'NL', 'NO', 'PL',
        'PT', 'RO', 'SA', 'SE', 'SG', 'SI', 'SK', 'TR', 'UK', 'US',
      ];

      this.init();
    }

    /**
     * Translation helper. Returns the translated string for a key,
     * falling back to the key itself if not found.
     * Decodes HTML entities (e.g. &#39;) that Shopify's | t filter may introduce.
     */
    t(key) {
      const val = this.translations[key] || key;
      if (val.includes('&')) {
        const el = document.createElement('textarea');
        el.innerHTML = val;
        return el.value;
      }
      return val;
    }

    init() {
      this.populateCountrySelect();
      this.bindEvents();
      this.checkUrlParams();
    }

    /**
     * Rellena el <select id="returns-country"> con un <option> por cada
     * código ISO de SUPPORTED_COUNTRIES. El nombre visible se obtiene de
     * Intl.DisplayNames en el idioma activo (auto-traducción del navegador,
     * sin necesidad de mantener nombres de país en cada locale).
     * Fallback: si Intl.DisplayNames no está disponible (navegadores
     * antiguos) se muestra el propio código ISO.
     *
     * El value del option es el código ISO de 2 letras → el backend lo
     * recibe ya normalizado, sin pasar por countryNameToCode().
     */
    populateCountrySelect() {
      const select = document.getElementById('returns-country');
      if (!select) return;

      let displayNames = null;
      try {
        if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
          displayNames = new Intl.DisplayNames([this.locale, 'es', 'en'], { type: 'region' });
        }
      } catch (e) {
        displayNames = null;
      }

      const entries = this.SUPPORTED_COUNTRIES.map((code) => ({
        code,
        name: (displayNames && displayNames.of(code)) || code,
      }));

      // Ordenar por nombre localizado (Intl.Collator respeta acentos/ñ).
      const collator = new Intl.Collator(this.locale, { sensitivity: 'base' });
      entries.sort((a, b) => collator.compare(a.name, b.name));

      const frag = document.createDocumentFragment();
      entries.forEach(({ code, name }) => {
        const opt = document.createElement('option');
        opt.value = code;
        opt.textContent = name;
        frag.appendChild(opt);
      });
      select.appendChild(frag);
    }

    bindEvents() {
      // Step 1: Lookup
      const lookupBtn = document.getElementById('returns-lookup-btn');
      if (lookupBtn) {
        lookupBtn.addEventListener('click', () => this.lookupOrder());
      }

      // Enter key on lookup form
      const orderInput = document.getElementById('returns-order-id');
      const emailInput = document.getElementById('returns-email');
      [orderInput, emailInput].forEach(input => {
        if (input) {
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this.lookupOrder();
          });
        }
      });

      // Step navigation
      document.getElementById('returns-step1-next')?.addEventListener('click', () => this.goToStep(2));
      document.getElementById('returns-step2-prev')?.addEventListener('click', () => this.goToStep(1));
      document.getElementById('returns-step2-next')?.addEventListener('click', () => this.validateAndGoStep3());
      document.getElementById('returns-step3-prev')?.addEventListener('click', () => this.goToStep(2));
      document.getElementById('returns-step3-next')?.addEventListener('click', () => this.validateAndGoStep4());
      document.getElementById('returns-step4-prev')?.addEventListener('click', () => this.goToStep(3));
      document.getElementById('returns-step4-next')?.addEventListener('click', () => this.validateAndGoStep5());
      document.getElementById('returns-step5-prev')?.addEventListener('click', () => this.goToStep(4));

      // DHL check
      document.getElementById('returns-dhl-check-btn')?.addEventListener('click', () => this.checkDHL());

      // Submit
      document.getElementById('returns-submit-btn')?.addEventListener('click', () => this.submitReturn());
    }

    checkUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const orderId = params.get('order_id');
      const email = params.get('email');
      const token = params.get('token');

      if (orderId) {
        document.getElementById('returns-order-id').value = orderId;

        if (email) {
          document.getElementById('returns-email').value = email;
          this.autoLookupAndSkip(orderId, email);
        } else if (token) {
          this.lookupOrderByToken(orderId, token);
        }
      }
    }

    /**
     * Resuelve el mensaje de error de un lookup fallido (success=false).
     * Si el backend manda un error_code conocido (p.ej. order_not_fulfilled:
     * pedido aún no enviado), usa la traducción; si no, el error crudo del
     * backend y por último el genérico "pedido no encontrado".
     */
    lookupErrorMessage(response) {
      if (response && response.error_code === 'order_not_fulfilled') {
        return this.t('error_order_not_fulfilled');
      }
      return (response && response.error) || this.t('error_order_not_found');
    }

    async autoLookupAndSkip(orderId, email) {
      this.showLoading('returns-lookup-loading');

      try {
        const response = await this.apiCall('lookup', {
          orderId: orderId.replace('#', ''),
          email: email
        });

        if (!response.success) {
          this.showError('returns-lookup-error', this.lookupErrorMessage(response));
          return;
        }

        this.orderData = response.order;
        this.renderOrderInfo();
        this.goToStep(2);
      } catch (err) {
        this.showError('returns-lookup-error', this.t('error_search_failed'));
      } finally {
        this.hideLoading('returns-lookup-loading');
      }
    }

    async lookupOrder() {
      const orderId = document.getElementById('returns-order-id').value.trim();
      const email = document.getElementById('returns-email').value.trim();

      if (!orderId || !email) {
        this.showError('returns-lookup-error', this.t('error_enter_order_email'));
        return;
      }

      this.hideError('returns-lookup-error');
      this.showLoading('returns-lookup-loading');
      this.hideElement('returns-order-info');

      try {
        const response = await this.apiCall('lookup', {
          orderId: orderId.replace('#', ''),
          email: email
        });

        if (!response.success) {
          this.showError('returns-lookup-error', this.lookupErrorMessage(response));
          return;
        }

        this.orderData = response.order;
        this.renderOrderInfo();
      } catch (err) {
        this.showError('returns-lookup-error', this.t('error_search_failed'));
      } finally {
        this.hideLoading('returns-lookup-loading');
      }
    }

    async lookupOrderByToken(orderId, token) {
      this.showLoading('returns-lookup-loading');

      try {
        const response = await this.apiCall('lookup', {
          orderId: orderId.replace('#', ''),
          token: token
        });

        if (!response.success) {
          this.showError('returns-lookup-error', this.lookupErrorMessage(response));
          return;
        }

        this.orderData = response.order;
        this.renderOrderInfo();
      } catch (err) {
        this.showError('returns-lookup-error', this.t('error_search_failed_manual'));
      } finally {
        this.hideLoading('returns-lookup-loading');
      }
    }

    renderOrderInfo() {
      const order = this.orderData;
      document.getElementById('returns-order-name').textContent = order.name;
      document.getElementById('returns-order-date').textContent = new Date(order.createdAt).toLocaleDateString(this.locale, {
        year: 'numeric', month: 'long', day: 'numeric'
      });
      document.getElementById('returns-order-email').textContent = order.email || '';

      // Show/hide "all items blocked" warning on step 1
      const step1BlockedEl = document.getElementById('returns-order-all-blocked');
      if (step1BlockedEl) {
        step1BlockedEl.style.display = order.all_items_blocked ? 'block' : 'none';
      }

      // Disable "next" button on step 1 if all items are blocked
      const nextBtn = document.getElementById('returns-step1-next');
      if (nextBtn && order.all_items_blocked) {
        nextBtn.disabled = true;
        nextBtn.classList.add('disabled');
      } else if (nextBtn) {
        nextBtn.disabled = false;
        nextBtn.classList.remove('disabled');
      }

      this.showElement('returns-order-info');

      // Pre-fill address from order shipping address.
      // El campo country es ahora un <select> con value=ISO. Usamos
      // countryCodeV2 (ya alpha-2) que viene en la respuesta de Shopify.
      // Fallback: si el código no aparece en SUPPORTED_COUNTRIES o falta,
      // se deja sin seleccionar y el cliente elige manualmente.
      if (order.shippingAddress) {
        const addr = order.shippingAddress;
        document.getElementById('returns-name').value = addr.name || '';
        document.getElementById('returns-email-addr').value = order.email || '';
        document.getElementById('returns-address1').value = addr.address1 || '';
        document.getElementById('returns-address2').value = addr.address2 || '';
        document.getElementById('returns-city').value = addr.city || '';
        document.getElementById('returns-zip').value = addr.zip || '';

        const countrySelect = document.getElementById('returns-country');
        const code = (addr.countryCodeV2 || addr.country_code || '').toUpperCase();
        if (countrySelect && code && this.SUPPORTED_COUNTRIES.includes(code)) {
          countrySelect.value = code;
        }

        document.getElementById('returns-phone').value = addr.phone || '';
      }
    }

    // --- Step 2: Items ---

    renderLineItems() {
      const container = document.getElementById('returns-items-list');
      if (!this.orderData || !this.orderData.lineItems) return;

      // Show banner if all items are blocked
      const bannerEl = document.getElementById('returns-all-blocked-banner');
      if (bannerEl) {
        bannerEl.style.display = this.orderData.all_items_blocked ? 'block' : 'none';
      }

      // Disable "next" button if all items are blocked
      const nextBtn = document.getElementById('returns-step2-next');
      if (nextBtn && this.orderData.all_items_blocked) {
        nextBtn.disabled = true;
        nextBtn.classList.add('disabled');
      } else if (nextBtn) {
        nextBtn.disabled = false;
        nextBtn.classList.remove('disabled');
      }

      container.innerHTML = this.orderData.lineItems.map((item, index) => {
        const isBlocked = item.blocked === true;
        const availableQty = item.available_quantity ?? item.quantity;
        const maxQty = isBlocked ? 0 : availableQty;

        const qtyOptions = Array.from({ length: maxQty }, (_, i) => i + 1)
          .map(n => `<option value="${n}"${n === maxQty ? ' selected' : ''}>${n}</option>`)
          .join('');

        const imageHtml = item.image
          ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy">`
          : '';

        let badgeHtml = '';
        if (isBlocked) {
          if (item.not_returnable) {
            // Producto no retornable (perfumes, joyas, etc.) — motivo distinto a "ya en una devolución".
            badgeHtml = `<div class="returns-item-badge returns-item-badge--blocked">${this.escapeHtml(this.t('item_not_returnable'))}</div>`;
          } else {
            const names = (item.blocked_return_names || []).join(', ');
            badgeHtml = `<div class="returns-item-badge returns-item-badge--blocked">${this.escapeHtml(this.t('item_in_return'))}${names ? ' (' + this.escapeHtml(names) + ')' : ''}</div>`;
          }
        } else if (item.partial_return) {
          const names = (item.partial_return_names || []).join(', ');
          badgeHtml = `<div class="returns-item-badge returns-item-badge--partial">${this.escapeHtml(this.t('item_partial_return').replace('{qty}', item.partial_return_quantity))}${names ? ' (' + this.escapeHtml(names) + ')' : ''}</div>`;
        }

        const rowClass = `returns-item-row${isBlocked ? ' returns-item-row--blocked' : ''}`;

        return `
          <div class="${rowClass}" data-index="${index}" data-item-id="${item.id}">
            <div class="returns-item-checkbox">
              <input type="checkbox" id="returns-item-${index}" data-index="${index}"${isBlocked ? ' disabled' : ''}>
            </div>
            <div class="returns-item-image">${imageHtml}</div>
            <div class="returns-item-info">
              <div class="returns-item-title">${this.escapeHtml(item.title)}</div>
              <div class="returns-item-variant">${this.escapeHtml(item.variantTitle || '')}</div>
              <div class="returns-item-price">${item.price} ${item.currency || 'EUR'}</div>
              ${badgeHtml}
            </div>
            <div class="returns-item-qty">
              ${isBlocked ? '' : `<label for="returns-qty-${index}">${this.escapeHtml(this.t('qty_label'))}</label>
              <select id="returns-qty-${index}" data-index="${index}">${qtyOptions}</select>`}
            </div>
          </div>
        `;
      }).join('');
    }

    validateAndGoStep3() {
      const checkboxes = document.querySelectorAll('#returns-items-list input[type="checkbox"]:checked');
      if (checkboxes.length === 0) {
        this.showError('returns-items-error', this.t('error_select_product'));
        return;
      }

      this.hideError('returns-items-error');
      this.selectedItems = [];

      checkboxes.forEach(cb => {
        const index = parseInt(cb.dataset.index);
        const qty = parseInt(document.getElementById(`returns-qty-${index}`).value);
        this.selectedItems.push({
          ...this.orderData.lineItems[index],
          returnQuantity: qty,
          index: index
        });
      });

      this.renderReasons();
      this.goToStep(3);
    }

    // --- Step 3: Reasons ---

    renderReasons() {
      const container = document.getElementById('returns-reasons-list');

      container.innerHTML = this.selectedItems.map((item, i) => {
        const options = this.RETURN_REASONS.map(r =>
          `<option value="${r.value}">${this.escapeHtml(r.label)}</option>`
        ).join('');

        const imageHtml = item.image
          ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy">`
          : '';

        return `
          <div class="returns-reason-row" data-item-index="${item.index}">
            <div class="returns-reason-product-info">
              <div class="returns-item-image">${imageHtml}</div>
              <div class="returns-item-info">
                <div class="returns-item-title">${this.escapeHtml(item.title)}</div>
                <div class="returns-item-variant">${this.escapeHtml(item.variantTitle || '')}</div>
                <div class="returns-item-price">${item.price} ${item.currency || 'EUR'}</div>
              </div>
            </div>
            <div class="returns-reason-select">
              <select id="returns-reason-${i}" data-reason-index="${i}">
                ${options}
              </select>
            </div>
            <div class="returns-reason-note" id="returns-reason-note-${i}">
              <textarea id="returns-reason-text-${i}" placeholder="${this.escapeHtml(this.t('reason_note_placeholder'))}"></textarea>
            </div>
          </div>
        `;
      }).join('');

      // El campo de comentario aparece al seleccionar CUALQUIER motivo (value
      // no vacío) y se oculta si se vuelve al placeholder. Solo es obligatorio
      // para "other" (ver validateAndGoStep4).
      container.querySelectorAll('select').forEach(select => {
        select.addEventListener('change', (e) => {
          const idx = e.target.dataset.reasonIndex;
          const noteEl = document.getElementById(`returns-reason-note-${idx}`);
          if (noteEl) {
            noteEl.classList.toggle('visible', e.target.value !== '');
          }
        });
      });
    }

    validateAndGoStep4() {
      this.reasons = {};

      let missingReason = false;
      let missingNote = false;

      this.selectedItems.forEach((item, i) => {
        const reason = document.getElementById(`returns-reason-${i}`).value;
        if (!reason) {
          missingReason = true;
          return;
        }
        const note = document.getElementById(`returns-reason-text-${i}`)?.value?.trim() || '';
        if (reason === 'other' && !note) {
          missingNote = true;
          return;
        }
        this.reasons[item.index] = { reason, note };
      });

      if (missingReason) {
        this.showError('returns-reasons-error', this.t('error_select_reason'));
        return;
      }

      if (missingNote) {
        this.showError('returns-reasons-error', this.t('error_describe_reason'));
        return;
      }

      this.hideError('returns-reasons-error');
      this.goToStep(4);
    }

    // --- Step 4: Address + DHL ---

    validateAndGoStep5() {
      const address1 = document.getElementById('returns-address1').value.trim();
      const city = document.getElementById('returns-city').value.trim();
      const zip = document.getElementById('returns-zip').value.trim();
      const country = document.getElementById('returns-country').value.trim();

      if (!address1 || !city || !zip || !country) {
        this.showError('returns-dhl-error', this.t('error_complete_address'));
        return;
      }

      // Exigir un método de devolución elegido. Excepción: DHL caído o sin
      // opciones para la dirección (dhlManualAllowed), donde los textos ya
      // dicen al cliente que puede continuar y gestionarlo manualmente.
      if (!this.dhlOption && !this.dhlManualAllowed) {
        this.showError('returns-dhl-error', this.t('error_select_dhl_option'));
        return;
      }

      // Validate home pickup (label or QR) requires date
      if (this.dhlOption === 'pickup' || this.dhlOption === 'pickup-qr') {
        if (!this.pickupDate) {
          this.showError('returns-dhl-error', this.t('error_select_pickup_datetime'));
          return;
        }
      }

      this.address = {
        address1: address1,
        address2: document.getElementById('returns-address2').value.trim(),
        city: city,
        zip: zip,
        country: country,
        phone: document.getElementById('returns-phone').value.trim()
      };

      this.hideError('returns-dhl-error');
      this.renderSummary();
      this.goToStep(5);
    }

    async checkDHL() {
      const address1 = document.getElementById('returns-address1').value.trim();
      const city = document.getElementById('returns-city').value.trim();
      const zip = document.getElementById('returns-zip').value.trim();
      const country = document.getElementById('returns-country').value.trim();

      if (!address1 || !city || !zip || !country) {
        this.showError('returns-dhl-error', this.t('error_complete_address_dhl'));
        return;
      }

      this.hideError('returns-dhl-error');
      this.showLoading('returns-dhl-loading');
      this.hideElement('returns-dhl-result');

      // Al (re)consultar se oculta la lista de opciones: cualquier selección
      // anterior deja de ser visible/válida, así que se limpia y se vuelve a
      // bloquear "Siguiente" hasta que el cliente elija sobre el resultado nuevo.
      this.dhlOption = null;
      this.dhlManualAllowed = false;
      this.setStep4NextEnabled(false);

      try {
        const response = await this.apiCall('dhl-check', {
          address: { address1, city, zip, country }
        });

        if (!response.success) {
          this.showError('returns-dhl-error', response.error || this.t('error_dhl_check'));
          return;
        }

        this.dhlResult = response.data;
        this.pickupWindow = response.data.pickupWindow || null;
        this.pickupFee = response.data.pickupFee || null;
        this.renderDHLResult(response.data);
      } catch (err) {
        // El texto del fallback promete que se puede continuar sin DHL:
        // desbloqueamos "Siguiente" para la gestión manual.
        this.dhlManualAllowed = true;
        this.setStep4NextEnabled(true);
        this.showError('returns-dhl-error', this.t('error_dhl_check_fallback'));
      } finally {
        this.hideLoading('returns-dhl-loading');
      }
    }

    renderDHLResult(result) {
      const container = document.getElementById('returns-dhl-result');
      this.servicePoints = result.servicePoints || [];
      this.selectedServicePoint = null;
      const hasServicePoints = result.servicePoints && result.servicePoints.length > 0;
      const spListHtml = hasServicePoints
        ? `<div class="returns-dhl-sp-section" style="display:none;">${this.renderServicePointsList(result.servicePoints)}</div>`
        : '';

      // SVG icons (extracted to keep markup readable below)
      const ICON_QR = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/>
        <line x1="21" y1="14" x2="21" y2="21"/><line x1="14" y1="21" x2="21" y2="21"/></svg>`;
      const ICON_LABEL = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/></svg>`;
      const ICON_PICKUP = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="1" y="6" width="22" height="15" rx="2"/><path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        <line x1="12" y1="11" x2="12" y2="16"/><line x1="9.5" y1="13.5" x2="14.5" y2="13.5"/></svg>`;
      const ICON_PICKUP_QR = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="1" y="6" width="22" height="15" rx="2"/><path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        <rect x="9" y="11" width="6" height="6"/><line x1="11" y1="13" x2="11" y2="13.01"/></svg>`;

      // renderCard(opt, icon, hasBadge, extraInside) → string
      // priceFee param: hoy SIEMPRE oculto. El precio de recogida está
      // comentado a propósito (devolución sin recargo al cliente).
      // Para reactivarlo: restaurar las dos ramas comentadas + el bloque
      // pickupFee del backend (controller + frontend renderSummary/Success).
      const card = (opt, icon, titleKey, descKey, hasBadge, priceFee, extraInside = '') => {
        const titleHtml = this.escapeHtml(this.t(titleKey))
          // + (priceFee && this.pickupFee && this.pickupFee.amount > 0
          //     ? ` <span class="returns-dhl-price">${this.formatPrice(this.pickupFee.amount, this.pickupFee.currency)}</span>`
          //     : '')
          + (hasBadge ? ` <span class="returns-dhl-badge">${this.escapeHtml(this.t('dhl_recommended'))}</span>` : '');
        // const feeNoteHtml = priceFee && this.pickupFee && this.pickupFee.amount > 0
        //   ? `<p class="returns-dhl-fee-note returns-dhl-option-desc" style="display:none;">${this.escapeHtml(this.t('dhl_pickup_fee_note'))}</p>`
        //   : '';
        const feeNoteHtml = '';
        return `
          <div class="returns-dhl-option" data-dhl-option="${opt}"
               onclick="window._returnsWizard.selectDHLOption(this, '${opt}')">
            <div class="returns-dhl-option-header">
              <div class="returns-dhl-option-icon">${icon}</div>
              <div class="returns-dhl-option-text">
                <h5>${titleHtml}</h5>
                <p class="returns-dhl-option-desc" style="display:none;">${this.escapeHtml(this.t(descKey))}</p>
                ${feeNoteHtml}
              </div>
              <div class="returns-dhl-option-radio"><span></span></div>
            </div>
            ${extraInside}
          </div>
        `;
      };

      let html = '';

      // Orden definido: Service point QR (Preferred) → Service point label
      // → Home pickup QR → Home pickup label.
      // Filtrado por país (backend devuelve availableOptions): FR solo home
      // pickup; label-free se confirma dinámicamente via DHL /rates.
      const availableOptions = result.availableOptions || ['qr', 'label', 'pickup-qr', 'pickup'];
      const canShow = (opt) => availableOptions.includes(opt);

      // Encabezado fijo antes de las cards, como pidió DHL.
      html += `<p class="returns-dhl-intro">${this.escapeHtml(this.t('dhl_select_option'))}</p>`;

      if (canShow('qr'))    html += card('qr',    ICON_QR,    'dhl_qr_title',    'dhl_qr_description',    true,  false, spListHtml);
      if (canShow('label')) html += card('label', ICON_LABEL, 'dhl_label_title', 'dhl_label_description', false, false, spListHtml);

      const showPickupQr = result.pickupAvailable && canShow('pickup-qr');
      const showPickup   = result.pickupAvailable && canShow('pickup');

      if (showPickupQr || showPickup) {
        if (showPickupQr) html += card('pickup-qr', ICON_PICKUP_QR, 'dhl_pickup_qr_title', 'dhl_pickup_qr_description', false, true);
        if (showPickup)   html += card('pickup',    ICON_PICKUP,    'dhl_pickup_title',    'dhl_pickup_description',    false, true);

        // Panel pickup-details compartido por pickup-qr y pickup (se muestra
        // cuando se selecciona cualquiera de los dos).
        const pickupDates = result.pickupDates || [];
        const pw = result.pickupWindow || this.pickupWindow;
        const windowLabel = pw
          ? this.t('dhl_pickup_window').replace('{earliest}', pw.earliest).replace('{latest}', pw.latest)
          : '';
        // Franja horaria compacta que se muestra DENTRO de cada botón de día.
        const slotText = pw ? `${pw.earliest}–${pw.latest}` : '';
        // La ventana horaria va dentro de cada día; solo se mantiene la línea
        // suelta como fallback cuando DHL no devuelve días concretos.
        const showLooseWindow = windowLabel && pickupDates.length === 0;
        html += `
          <div class="returns-pickup-details" id="returns-pickup-details" style="display:none;">
            ${showLooseWindow ? `<p class="returns-pickup-window">${this.escapeHtml(windowLabel)}</p>` : ''}
            <div class="returns-pickup-section">
              <label>${this.escapeHtml(this.t('dhl_pickup_date'))}</label>
              <div class="returns-pickup-date-grid">
                ${pickupDates.map(d => {
                  const dLabel = this.formatPickupDateLabel(d.date);
                  const btnLabel = slotText ? `${dLabel} · ${slotText}` : dLabel;
                  return `
                  <button type="button" class="returns-pickup-date-btn" data-date="${d.date}"
                          onclick="window._returnsWizard.selectPickupDate(this, '${d.date}')">${this.escapeHtml(btnLabel)}</button>
                `;
                }).join('')}
              </div>
            </div>
          </div>
        `;
      }

      // "Sin opciones" si DHL no devolvió ni service points ni pickup,
      // o si el país no permite ninguna de las opciones que tendríamos
      // disponibles (caso edge: pickup disponible pero país sin home pickup
      // configurado, o service points sin opciones drop-off permitidas).
      const hasAnyDropOff = hasServicePoints && (canShow('qr') || canShow('label'));
      const hasAnyPickup = showPickupQr || showPickup;
      if (!hasAnyDropOff && !hasAnyPickup) {
        html = `<p>${this.escapeHtml(this.t('dhl_no_options'))}</p>`;
        this.dhlOption = null;
        // Sin opciones que elegir: el texto invita a continuar y gestionar
        // el envío manualmente, así que "Siguiente" queda desbloqueado.
        this.dhlManualAllowed = true;
        this.setStep4NextEnabled(true);
      } else {
        this.dhlOption = null;
        this.pickupDate = null;
        // Hay opciones: "Siguiente" bloqueado hasta que el cliente elija una.
        this.dhlManualAllowed = false;
        this.setStep4NextEnabled(false);
      }

      container.innerHTML = html;
      this.showElement('returns-dhl-result');
    }

    renderServicePointsList(servicePoints) {
      if (!servicePoints || servicePoints.length === 0) return '';

      return `
        <div class="returns-dhl-sp-list">
          <p class="returns-dhl-sp-list-title">${this.escapeHtml(this.t('dhl_nearby_points'))}</p>
          ${servicePoints.map((sp, i) => `
            <div class="returns-dhl-sp-item" data-sp-index="${i}"
                 onclick="window._returnsWizard.selectServicePoint(this, ${i})">
              <div class="returns-dhl-sp-radio"><span></span></div>
              <div class="returns-dhl-sp-body">
                <div class="returns-dhl-sp-name">${this.escapeHtml(sp.name)}</div>
                <div class="returns-dhl-sp-address">
                  ${this.escapeHtml(sp.address)}${sp.distance ? ` — ${sp.distance}` : ''}
                </div>
                ${sp.openingHours ? `<div class="returns-dhl-sp-hours">${this.renderOpeningHours(sp.openingHours)}</div>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }

    /**
     * Selecciona un Service Point (drop-off QR/etiqueta). La lista se renderiza
     * duplicada dentro de las tarjetas QR y etiqueta; marcamos solo el ítem
     * clicado dentro de SU lista y guardamos el punto (opcional: informativo
     * para resumen + email). El índice apunta a this.servicePoints.
     */
    selectServicePoint(el, index) {
      const list = el.closest('.returns-dhl-sp-list');
      if (list) {
        list.querySelectorAll('.returns-dhl-sp-item').forEach(it => it.classList.remove('selected'));
      }
      el.classList.add('selected');
      this.selectedServicePoint = this.servicePoints[index] || null;
    }

    renderOpeningHours(hours) {
      if (!hours || typeof hours !== 'object') return '';

      const parts = [];

      for (const [day, slots] of Object.entries(hours)) {
        const label = this.dayMap[day] || this.dayMap[day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()] || day;
        const times = slots.map(s => `${s.open || ''}-${s.close || ''}`).join(', ');
        if (times && times !== '-') {
          parts.push(`${label}: ${times}`);
        }
      }

      return parts.join(' | ');
    }

    /**
     * Habilita/deshabilita el "Siguiente" del paso 4. Arranca deshabilitado
     * desde el Liquid y solo se abre al elegir un método de devolución (o en
     * los casos de gestión manual, ver dhlManualAllowed).
     */
    setStep4NextEnabled(enabled) {
      const btn = document.getElementById('returns-step4-next');
      if (!btn) return;
      btn.disabled = !enabled;
      btn.classList.toggle('disabled', !enabled);
    }

    selectDHLOption(element, option) {
      const changed = this.dhlOption !== option;
      // Hide all details and deselect all options
      document.querySelectorAll('.returns-dhl-option').forEach(el => {
        el.classList.remove('selected');
        el.querySelectorAll('.returns-dhl-option-desc, .returns-dhl-sp-section').forEach(d => d.style.display = 'none');
      });
      element.classList.add('selected');
      this.dhlOption = option;
      this.setStep4NextEnabled(true);

      // Show details for the selected option
      element.querySelectorAll('.returns-dhl-option-desc, .returns-dhl-sp-section').forEach(d => d.style.display = '');

      // Only reset pickup selections when actually changing option
      if (changed) {
        this.pickupDate = null;
        document.querySelectorAll('.returns-pickup-date-btn').forEach(b => b.classList.remove('active'));
        // Reset del Service Point elegido al cambiar de método (la lista está
        // duplicada en cada tarjeta; limpiamos ambas).
        this.selectedServicePoint = null;
        document.querySelectorAll('.returns-dhl-sp-item.selected').forEach(it => it.classList.remove('selected'));
      }

      // Show/hide pickup details (date selectors) — compartido para las dos
      // opciones de home pickup (con QR y con etiqueta).
      const pickupDetails = document.getElementById('returns-pickup-details');
      if (pickupDetails) {
        const isHomePickup = option === 'pickup' || option === 'pickup-qr';
        pickupDetails.style.display = isHomePickup ? 'block' : 'none';
      }
    }

    selectPickupDate(element, date) {
      document.querySelectorAll('.returns-pickup-date-btn').forEach(b => b.classList.remove('active'));
      element.classList.add('active');
      this.pickupDate = date;
    }

    /**
     * Formatea una fecha de recogida (YYYY-MM-DD) al idioma activo, p.ej.
     * "mié 22 jul" (es) / "dc. 22 jul." (ca) / "Wed 22 Jul" (en). El backend
     * envía d.label en español fijo; lo ignoramos y localizamos aquí desde
     * d.date. Parseamos como fecha LOCAL (no UTC) para evitar el off-by-one.
     */
    formatPickupDateLabel(dateStr) {
      if (!dateStr) return '';
      const parts = String(dateStr).split('-').map(Number);
      if (parts.length !== 3 || parts.some(isNaN)) return dateStr;
      const [y, m, d] = parts;
      try {
        const label = new Date(y, m - 1, d).toLocaleDateString(this.locale, {
          weekday: 'short', day: 'numeric', month: 'short'
        });
        // Quitamos comas que algunos locales insertan ("mié, 22 jul") para
        // mantener el estilo compacto del chip.
        return label.replace(/,/g, '');
      } catch (e) {
        return dateStr;
      }
    }

    // --- Step 5: Summary ---

    renderSummary() {
      const container = document.getElementById('returns-summary');

      // Order section
      let html = `
        <div class="returns-summary-section">
          <h4>${this.escapeHtml(this.t('summary_order'))}</h4>
          <p><strong>${this.escapeHtml(this.orderData.name)}</strong></p>
        </div>
        <div class="returns-summary-section">
          <h4>${this.escapeHtml(this.t('summary_products'))}</h4>
      `;

      this.selectedItems.forEach(item => {
        const reason = this.reasons[item.index];
        const reasonLabel = this.RETURN_REASONS.find(r => r.value === reason.reason)?.label || reason.reason;
        html += `
          <div class="returns-summary-item">
            <div class="returns-summary-item-info">
              <div class="returns-summary-item-title">${this.escapeHtml(item.title)}</div>
              <div class="returns-summary-item-detail">
                ${item.variantTitle ? this.escapeHtml(item.variantTitle) + ' — ' : ''}
                ${this.escapeHtml(this.t('summary_quantity'))}: ${item.returnQuantity} — ${this.escapeHtml(this.t('summary_reason'))}: ${this.escapeHtml(reasonLabel)}
                ${reason.note ? '<br>' + this.escapeHtml(this.t('summary_note')) + ': ' + this.escapeHtml(reason.note) : ''}
              </div>
            </div>
          </div>
        `;
      });

      html += `</div>`;

      // Address section
      html += `
        <div class="returns-summary-section">
          <h4>${this.escapeHtml(this.t('summary_address'))}</h4>
          <p>
            ${this.escapeHtml(this.address.address1)}<br>
            ${this.address.address2 ? this.escapeHtml(this.address.address2) + '<br>' : ''}
            ${this.escapeHtml(this.address.city)}, ${this.escapeHtml(this.address.zip)}<br>
            ${this.escapeHtml(this.address.country)}
            ${this.address.phone ? '<br>' + this.escapeHtml(this.address.phone) : ''}
          </p>
        </div>
      `;

      // DHL section
      if (this.dhlOption) {
        const dhlLabels = {
          qr: this.t('summary_dhl_qr'),
          label: this.t('summary_dhl_label'),
          'pickup-qr': this.t('summary_dhl_pickup_qr'),
          pickup: this.t('summary_dhl_pickup')
        };
        let dhlDetail = dhlLabels[this.dhlOption] || this.dhlOption;
        const isHomePickup = this.dhlOption === 'pickup' || this.dhlOption === 'pickup-qr';
        if (isHomePickup && this.pickupDate) {
          dhlDetail += `<br>${this.escapeHtml(this.formatPickupDateLabel(this.pickupDate))}`;
          if (this.pickupWindow) {
            const windowLabel = this.t('dhl_pickup_window').replace('{earliest}', this.pickupWindow.earliest).replace('{latest}', this.pickupWindow.latest);
            dhlDetail += ` — ${this.escapeHtml(windowLabel)}`;
          }
        }
        // Punto de entrega elegido (opcional) en drop-off QR/etiqueta.
        const isDropOff = this.dhlOption === 'qr' || this.dhlOption === 'label';
        if (isDropOff && this.selectedServicePoint) {
          const sp = this.selectedServicePoint;
          dhlDetail += `<br><strong>${this.escapeHtml(this.t('summary_dropoff_point'))}:</strong> ${this.escapeHtml(sp.name || '')}`;
          if (sp.address) {
            dhlDetail += `<br>${this.escapeHtml(sp.address)}`;
          }
          if (sp.openingHours) {
            dhlDetail += `<br><span class="returns-summary-hours">${this.renderOpeningHours(sp.openingHours)}</span>`;
          }
        }
        // Precio de recogida — comentado. Reactivar restaurando esta línea
        // y el bloque pickup_fee_summary del backend + renderSuccess.
        // const feeLine = isHomePickup && this.pickupFee && this.pickupFee.amount > 0
        //   ? `<p class="returns-summary-fee">${this.escapeHtml(this.t('dhl_pickup_fee_summary'))}: <strong>${this.formatPrice(this.pickupFee.amount, this.pickupFee.currency)}</strong></p>`
        //   : '';
        html += `
          <div class="returns-summary-section">
            <h4>${this.escapeHtml(this.t('summary_shipping_method'))}</h4>
            <p>${dhlDetail}</p>
          </div>
        `;
      }

      container.innerHTML = html;

      // Render policy checkbox
      this.policyAccepted = false;
      const policyContainer = document.getElementById('returns-policy');
      if (policyContainer) {
        policyContainer.innerHTML = `
          <label class="returns-policy-label">
            <input type="checkbox" class="returns-policy-checkbox" id="returns-policy-checkbox"
                   onchange="window._returnsWizard.togglePolicy(this.checked)">
            ${this.escapeHtml(this.t('policy_text'))} <a href="/policies/refund-policy" target="_blank">${this.escapeHtml(this.t('policy_link_text'))}</a>.
          </label>
        `;
      }

      this.updateSubmitButton();
    }

    togglePolicy(checked) {
      this.policyAccepted = checked;
      this.updateSubmitButton();
    }

    updateSubmitButton() {
      const btn = document.getElementById('returns-submit-btn');
      if (!btn) return;

      if (this.policyAccepted) {
        btn.disabled = false;
        btn.classList.remove('disabled');
      } else {
        btn.disabled = true;
        btn.classList.add('disabled');
      }
    }

    // --- Submit ---

    async submitReturn() {
      // Guard contra doble-click: el flujo crea envío DHL + return Shopify
      // + email — un envío duplicado se traduce en 2 etiquetas reales.
      if (this._submitting) {
        return;
      }

      if (!this.policyAccepted) {
        this.showError('returns-submit-error', this.t('error_accept_policy'));
        return;
      }

      this._submitting = true;
      const submitBtn = document.getElementById('returns-submit-btn');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('disabled');
        submitBtn.setAttribute('aria-busy', 'true');
      }

      this.hideError('returns-submit-error');
      this.showLoading('returns-submit-loading');

      const items = this.selectedItems.map(item => ({
        lineItemId: item.id,
        quantity: item.returnQuantity,
        reason: this.reasons[item.index].reason,
        note: this.reasons[item.index].note || ''
      }));

      const payload = {
        orderId: this.orderData.id,
        orderName: this.orderData.name,
        items: items,
        address: this.address,
        dhlOption: this.dhlOption,
        // Idioma activo del storefront (request.locale.iso_code) para que el
        // email de confirmación salga en la lengua con la que el cliente está
        // haciendo la devolución, no en la del checkout del pedido.
        locale: this.locale
      };

      if (this.dhlOption === 'pickup' || this.dhlOption === 'pickup-qr') {
        payload.pickupDate = this.pickupDate;
        payload.pickupWindow = this.pickupWindow;
      }

      // Punto de entrega elegido (opcional) en drop-off QR/etiqueta → resumen + email.
      // Enviamos el horario ya formateado (localizado) para que el email no
      // tenga que reconstruirlo desde el objeto de DHL.
      if ((this.dhlOption === 'qr' || this.dhlOption === 'label') && this.selectedServicePoint) {
        const sp = this.selectedServicePoint;
        payload.servicePoint = {
          name: sp.name || '',
          address: sp.address || '',
          hoursText: sp.openingHours ? this.renderOpeningHours(sp.openingHours) : ''
        };
      }

      let succeeded = false;
      try {
        const response = await this.apiCall('create', payload);

        if (!response.success) {
          let errorMsg = response.error || this.t('error_create_return');
          if (response.conflicts && response.conflicts.length > 0) {
            const conflictNames = response.conflicts.map(c => c.title || c.lineItemId).join(', ');
            errorMsg += ' (' + conflictNames + ')';
          }
          if (response.reference) {
            errorMsg += ` (ref: ${response.reference})`;
          }
          this.showError('returns-submit-error', errorMsg);
          return;
        }

        succeeded = true;
        this.renderSuccess(response);
      } catch (err) {
        const errData = err.responseData || {};
        let msg;
        if (errData.conflicts) {
          msg = errData.error || this.t('error_create_return');
        } else if (err.code === 'TIMEOUT' || err.code === 'NETWORK' || err.code === 'BAD_JSON' || err.code === 'RATE_LIMITED') {
          msg = err.message;
        } else if (errData.error) {
          msg = errData.error;
          if (errData.reference) msg += ` (ref: ${errData.reference})`;
        } else {
          msg = this.t('error_submit_failed');
        }
        this.showError('returns-submit-error', msg);
      } finally {
        this.hideLoading('returns-submit-loading');
        // Solo liberamos el botón si NO hemos pasado a la pantalla de éxito
        // (en éxito ya no se ve, pero por si acaso el usuario navega back).
        if (!succeeded) {
          this._submitting = false;
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('disabled');
            submitBtn.removeAttribute('aria-busy');
          }
        }
      }
    }

    renderSuccess(response) {
      const container = document.getElementById('returns-success-info');
      const dhl = response.dhl || {};
      let dhlHtml = '';

      const isLabelFreeType = dhl.type === 'qr' || dhl.type === 'pickup-qr';
      const isHomePickupType = dhl.type === 'pickup' || dhl.type === 'pickup-qr';

      if (isLabelFreeType && dhl.qrCode) {
        // QR label-free: tanto service-point QR como home-pickup QR muestran
        // el código QR. Si además es pickup, añadimos los datos de la recogida.
        const pickup = dhl.pickup || {};
        const pickupBlock = isHomePickupType
          ? `
            <p>${this.escapeHtml(this.t('success_pickup_booked'))} <strong>${this.escapeHtml(pickup.pickupDate || '')}</strong>
               ${this.escapeHtml(this.t('success_pickup_between'))} <strong>${this.escapeHtml(pickup.readyByTime || '')}</strong> ${this.escapeHtml(this.t('success_pickup_and'))} <strong>${this.escapeHtml(pickup.closingTime || '')}</strong>.</p>
            ${pickup.confirmationNumber ? `<p>${this.escapeHtml(this.t('success_confirmation'))}: <strong>${this.escapeHtml(pickup.confirmationNumber)}</strong></p>` : ''}
            ${dhl.pickupError ? `<p class="returns-warning">${this.escapeHtml(dhl.pickupError)}</p>` : ''}
          `
          : '';
        dhlHtml = `
          <div class="returns-success-dhl">
            <div class="returns-qr">
              <img src="data:image/png;base64,${dhl.qrCode}" alt="QR Code DHL">
            </div>
            <p>${this.escapeHtml(this.t(isHomePickupType ? 'success_pickup_qr_instruction' : 'success_qr_instruction'))}</p>
            ${pickupBlock}
            ${dhl.trackingNumber ? `<p class="returns-tracking">${this.escapeHtml(this.t('success_tracking'))}: <strong>${this.escapeHtml(dhl.trackingNumber)}</strong></p>` : ''}
          </div>
        `;
      } else if (dhl.type === 'pickup') {
        const pickup = dhl.pickup || {};
        dhlHtml = `
          <div class="returns-success-dhl">
            <p>${this.escapeHtml(this.t('success_pickup_booked'))} <strong>${this.escapeHtml(pickup.pickupDate || '')}</strong>
               ${this.escapeHtml(this.t('success_pickup_between'))} <strong>${this.escapeHtml(pickup.readyByTime || '')}</strong> ${this.escapeHtml(this.t('success_pickup_and'))} <strong>${this.escapeHtml(pickup.closingTime || '')}</strong>.</p>
            ${pickup.confirmationNumber ? `<p>${this.escapeHtml(this.t('success_confirmation'))}: <strong>${this.escapeHtml(pickup.confirmationNumber)}</strong></p>` : ''}
            ${dhl.trackingNumber ? `<p class="returns-tracking">${this.escapeHtml(this.t('success_tracking'))}: <strong>${this.escapeHtml(dhl.trackingNumber)}</strong></p>` : ''}
            ${dhl.pickupError ? `<p class="returns-warning">${this.escapeHtml(dhl.pickupError)}</p>` : ''}
            ${dhl.label ? `<p><a href="data:application/pdf;base64,${dhl.label}" download="etiqueta-dhl.pdf" class="button outline">${this.escapeHtml(this.t('success_download_pdf'))}</a></p>` : ''}
          </div>
        `;
      } else if (dhl.type === 'label' && dhl.label) {
        dhlHtml = `
          <div class="returns-success-dhl">
            <p>${this.escapeHtml(this.t('success_label_instruction'))}</p>
            <p><a href="data:application/pdf;base64,${dhl.label}" download="etiqueta-dhl.pdf" class="button">${this.escapeHtml(this.t('success_download_pdf'))}</a></p>
            ${dhl.trackingNumber ? `<p class="returns-tracking">${this.escapeHtml(this.t('success_tracking'))}: <strong>${this.escapeHtml(dhl.trackingNumber)}</strong></p>` : ''}
          </div>
        `;
      } else if (dhl.trackingNumber) {
        dhlHtml = `
          <div class="returns-success-dhl">
            <p class="returns-tracking">${this.escapeHtml(this.t('success_tracking'))}: <strong>${this.escapeHtml(dhl.trackingNumber)}</strong></p>
          </div>
        `;
      }

      // Precio de recogida — comentado. Reactivar junto al backend para
      // volver a mostrar el recargo en la pantalla de éxito.
      // const feeHtml = response.pickupFee && response.pickupFee.amount > 0
      //   ? `<p class="returns-success-fee">${this.escapeHtml(this.t('dhl_pickup_fee_summary'))}: <strong>${this.formatPrice(response.pickupFee.amount, response.pickupFee.currency)}</strong></p>`
      //   : '';
      const feeHtml = '';

      // Avisos no-fatales: email no enviado / persist en BD falló.
      // El return YA existe en DHL+Shopify, no es bloqueante, pero el cliente
      // debe verlo claramente para guardar el tracking number.
      const warnings = [];
      if (response.emailError) warnings.push(response.emailError);
      if (response.persistError) warnings.push(response.persistError);
      if (response.dhlError) warnings.push(response.dhlError);

      const warningsHtml = warnings.length > 0
        ? warnings.map(w => `<p class="returns-warning">${this.escapeHtml(w)}</p>`).join('')
        : '';

      // El mensaje de éxito inserta el nº de pedido. Si la traducción incluye
      // el placeholder {order} (p.ej. alemán, donde el verbo va al final) se
      // sustituye ahí; si no, se añade en negrita al final (comportamiento
      // histórico del resto de idiomas).
      const orderStrong = `<strong>${this.escapeHtml(this.orderData.name)}</strong>`;
      const rawSuccessMsg = this.escapeHtml(this.t('success_message'));
      const successMessageHtml = rawSuccessMsg.includes('{order}')
        ? rawSuccessMsg.replace('{order}', orderStrong)
        : `${rawSuccessMsg} ${orderStrong}.`;

      container.innerHTML = `
        <p class="returns-success-id">${this.escapeHtml(this.t('success_return_label'))}: ${this.escapeHtml(response.returnName || response.returnId || this.t('success_registered'))}</p>
        <p>${successMessageHtml}</p>
        ${feeHtml}
        ${dhlHtml}
        ${warningsHtml}
      `;

      // Hide all steps, show success
      document.querySelectorAll('.returns-step').forEach(s => s.classList.remove('active'));
      document.querySelector('.returns-step[data-step="success"]').style.display = 'block';
      document.querySelector('.returns-step[data-step="success"]').classList.add('active');

      // Mark all progress steps as completed
      document.querySelectorAll('.returns-progress-step').forEach(s => s.classList.add('completed'));
      document.querySelectorAll('.returns-progress-line').forEach(l => l.classList.add('completed'));
    }

    // --- Navigation ---

    goToStep(step) {
      // When going to step 2, render items if not done
      if (step === 2 && this.orderData) {
        this.renderLineItems();
      }

      // Hide current step
      document.querySelectorAll('.returns-step').forEach(s => s.classList.remove('active'));

      // Show target step
      const targetStep = document.querySelector(`.returns-step[data-step="${step}"]`);
      if (targetStep) {
        targetStep.classList.add('active');
      }

      // Update progress bar
      document.querySelectorAll('.returns-progress-step').forEach(s => {
        const stepNum = parseInt(s.dataset.step);
        s.classList.remove('active', 'completed');
        if (stepNum === step) {
          s.classList.add('active');
        } else if (stepNum < step) {
          s.classList.add('completed');
        }
      });

      // Update progress lines
      document.querySelectorAll('.returns-progress-line').forEach((line, i) => {
        if (i < step - 1) {
          line.classList.add('completed');
        } else {
          line.classList.remove('completed');
        }
      });

      this.currentStep = step;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // --- API ---

    async apiCall(command, data) {
      const apiBase = this.config.apiBase || '/apps/returns';
      const controller = new AbortController();
      // Timeouts según comando: create implica DHL CreateShipment +
      // Shopify createReturn + email (puede tardar). dhl-check hace 2
      // llamadas DHL secuenciales (servicepoints + rates) de hasta 15s
      // cada una internamente. lookup es solo Shopify.
      const timeoutMs = command === 'create' ? 60000
        : command === 'dhl-check' ? 35000
        : 15000;
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      let response;
      try {
        response = await fetch(apiBase, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ command, ...data }),
          signal: controller.signal
        });
      } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          const e = new Error(this.t('error_timeout') || 'La operación está tardando demasiado. Por favor, vuelve a intentarlo.');
          e.code = 'TIMEOUT';
          throw e;
        }
        // Error de red (DNS, offline, CORS bloqueado…)
        const e = new Error(this.t('error_network') || 'No se ha podido conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.');
        e.code = 'NETWORK';
        throw e;
      }
      clearTimeout(timeoutId);

      if (response.status === 429) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.error || this.t('error_rate_limited') || 'Demasiados intentos. Por favor, espera unos segundos.');
        err.code = 'RATE_LIMITED';
        err.responseData = errorData;
        throw err;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.error || `HTTP ${response.status}`);
        err.code = `HTTP_${response.status}`;
        err.responseData = errorData;
        throw err;
      }

      // Body parse defensivo (puede llegar HTML si nginx interceptó la respuesta).
      try {
        return await response.json();
      } catch (e) {
        const err = new Error(this.t('error_bad_response') || 'Respuesta inválida del servidor. Inténtalo de nuevo.');
        err.code = 'BAD_JSON';
        throw err;
      }
    }

    // --- Helpers ---

    showError(id, message) {
      const el = document.getElementById(id);
      if (el) {
        el.textContent = message;
        el.style.display = 'block';
      }
    }

    hideError(id) {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    }

    showLoading(id) {
      const el = document.getElementById(id);
      if (el) el.style.display = 'flex';
    }

    hideLoading(id) {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    }

    showElement(id) {
      const el = document.getElementById(id);
      if (el) el.style.display = 'block';
    }

    hideElement(id) {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    formatPrice(amount, currency) {
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency }).format(amount);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window._returnsWizard = new ReturnsWizard();
    });
  } else {
    window._returnsWizard = new ReturnsWizard();
  }
})();